import { track } from "@amplitude/analytics-react-native";
import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { View } from "react-native";
import { FlatList } from "react-native-gesture-handler";
import CreateGatheringAddonDocument from "src/api/graphql/__generated__/documents/CreateGatheringAddonDocument";
import EditGatheringAddonDocument from "src/api/graphql/__generated__/documents/EditGatheringAddonDocument";
import SearchAppleMusicSongsDocument from "src/api/graphql/__generated__/documents/SearchAppleMusicSongsDocument";
import AudioPlayerSource from "src/components/(audio-player)/AudioPlayerSource";
import ListFooterComponent from "src/components/ListFooterComponent";
import OpacityView from "src/components/OpacityView";
import PillButton from "src/components/PillButton";
import Search from "src/components/Search";
import ToggleBadge from "src/components/ToggleBadge";
import Touchable from "src/components/Touchable";
import useStateRef from "src/hooks/useStateRef";
import { useInsideModal } from "src/modals";
import GatheringModal from "src/modals/configs/GatheringModal";
import useAudioPlayerStore from "src/stores/useAudioPlayerStore";
import { paddingLarge, paddingMedium, paddingSmall } from "src/styles/spacing";
import { Image, Text, useSafeArea } from "src/swsh-native";
import useDeleteGatheringAddon from "../hooks/useDeleteGatheringAddon";
import { EditAddonHorizontalPadding } from "../styles";

const RowHeight = 60;
const GatheringAppleMusicAddonEdit = () => {
	const {
		closeSelf,
		props: { variant, gatheringId, addonId, url: existingUrl, label: existingTitle },
	} = useInsideModal<GatheringModal["GatheringAddonEditModal"]>();
	const deleteGatheringAddon = useDeleteGatheringAddon();
	const [search, setSearch] = useState("");
	const { data, previousData, loading } = useQuery(SearchAppleMusicSongsDocument, {
		variables: {
			input: {
				search,
			},
		},
		fetchPolicy: "cache-and-network",
	});
	const { data: specificSearchData } = useQuery(SearchAppleMusicSongsDocument, {
		variables: {
			input: {
				search: existingTitle,
			},
		},
		fetchPolicy: "cache-first",
		skip: !existingTitle || !!search,
	});

	const songs = useMemo(() => {
		const firstSearchedSong = existingUrl
			? (specificSearchData?.searchAppleMusicSongs?.songs ?? []).find(
					(song) => song.url === existingUrl,
				)
			: null;
		const songs = [...((data ?? previousData)?.searchAppleMusicSongs?.songs ?? [])];
		const seenSongs = new Set<string>();
		const deduplicatedSongs = songs.filter((song) => {
			if (seenSongs.has(song.url)) return false;
			seenSongs.add(song.url);
			return true;
		});
		if (firstSearchedSong && !seenSongs.has(firstSearchedSong.url)) {
			deduplicatedSongs.unshift(firstSearchedSong);
		}
		return deduplicatedSongs;
	}, [data, previousData, specificSearchData, existingUrl]);
	const safeArea = useSafeArea();
	const [curSelection, setCurSelection, curSelectionRef] = useStateRef<{
		title: string;
		url: string;
		didChange: boolean;
		iconUrl?: string;
		displayIconUrl?: string;
		audioUrl?: string;
	} | null>(
		existingUrl && existingTitle
			? { title: existingTitle, url: existingUrl, didChange: false }
			: null,
	);

	const apolloClient = useApolloClient();
	const handleSave = useCallback(async () => {
		if (!curSelectionRef.current) return;
		const { title, url, iconUrl, audioUrl, didChange } = curSelectionRef.current;
		if (!didChange) {
			closeSelf();
			return;
		}
		if (addonId) {
			await apolloClient.mutate({
				mutation: EditGatheringAddonDocument,
				variables: {
					input: {
						addonId,
						gatheringId,
						url,
						label: title,
						iconUrl,
						audioUrl,
					},
				},
			});
		} else {
			await apolloClient.mutate({
				mutation: CreateGatheringAddonDocument,
				variables: {
					input: {
						gatheringId,
						variant,
						url,
						label: title,
						iconUrl,
						audioUrl,
					},
				},
			});
		}
		closeSelf();
	}, [addonId, gatheringId, closeSelf, apolloClient, variant, curSelectionRef]);

	const clearAudioPlayer = useAudioPlayerStore((state) => state.clear);
	return (
		<View
			style={{
				flex: 1,
				rowGap: paddingSmall,
			}}
		>
			{!!(curSelection?.audioUrl && curSelection?.displayIconUrl) && (
				<AudioPlayerSource
					audioUrl={curSelection.audioUrl}
					thumbnailUrl={curSelection.displayIconUrl}
				/>
			)}
			<View
				style={{
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
					paddingVertical: paddingSmall,
					paddingHorizontal: EditAddonHorizontalPadding,
				}}
			>
				<View
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						alignItems: "center",
						rowGap: paddingSmall,
					}}
				>
					<Text testID="EditAddonHeader" basic semibold>
						Add Song
					</Text>
				</View>
				{existingTitle || existingUrl ? (
					<PillButton
						testID="EditAddonRemove"
						onPress={deleteGatheringAddon}
						textProps={{ xs: true, semibold: true }}
						colorVariant="Destructive"
					>
						Delete
					</PillButton>
				) : (
					<PillButton
						testID="EditAddonClose"
						onPress={closeSelf}
						textProps={{ xs: true, semibold: true }}
					>
						Cancel
					</PillButton>
				)}
				<PillButton
					testID="EditAddonSave"
					onPress={handleSave}
					textProps={{ xs: true, semibold: true }}
					disabled={!curSelection}
				>
					Save
				</PillButton>
			</View>
			<View
				style={{
					flex: 1,
					paddingTop: paddingLarge,
				}}
			>
				<Search
					onDebouncedChangeText={setSearch}
					style={{
						height: "100%",
						marginHorizontal: EditAddonHorizontalPadding,
						alignSelf: "center",
					}}
				/>
				<FlatList
					style={{
						flex: 1,
						marginTop: paddingLarge,
					}}
					keyboardDismissMode="on-drag"
					data={songs}
					contentContainerStyle={{
						paddingBottom: safeArea.bottom + paddingSmall,
					}}
					ListFooterComponent={<ListFooterComponent loading={loading} />}
					renderItem={({ item: song }) => {
						const isSelected = Boolean(curSelection && curSelection.url === song.url);
						const imageSize = RowHeight - 2 * paddingMedium;
						return (
							<Touchable
								testID="Song"
								containerStyle={{
									width: "100%",
									height: RowHeight,
								}}
								onPress={() => {
									if (isSelected) {
										setCurSelection(null);
									} else {
										if (
											song.displayAlbumArtPreviewUrl &&
											song.audioPreviewUrl
										) {
											// New song should take priority
											clearAudioPlayer();
										}
										setCurSelection({
											title: song.name,
											url: song.url,
											iconUrl: song.albumArtPreviewUrl ?? undefined,
											audioUrl: song.audioPreviewUrl ?? undefined,
											displayIconUrl:
												song.displayAlbumArtPreviewUrl ?? undefined,
											didChange: true,
										});
										track("gathering_addon_appleMusic_songSelect", {
											title: song.name,
											url: song.url,
											artistName: song.artistName,
										});
									}
								}}
							>
								<OpacityView
									style={{
										flexDirection: "row",
										gap: paddingMedium,
										justifyContent: "space-between",
										alignItems: "center",
										paddingHorizontal: EditAddonHorizontalPadding,
									}}
								>
									<View
										style={{
											flexDirection: "row",
											gap: paddingLarge,
											alignItems: "center",
											flex: 1,
										}}
									>
										{song.displayAlbumArtPreviewUrl ? (
											<Image
												alt="Album cover"
												src={song.displayAlbumArtPreviewUrl}
												dim={{
													width: imageSize,
													height: imageSize,
												}}
											/>
										) : (
											<View
												style={{
													width: imageSize,
													height: imageSize,
												}}
											/>
										)}
										<View
											style={{
												flex: 1,
											}}
										>
											<Text semibold numberOfLines={1} ellipsizeMode="tail">
												{song.name}
											</Text>
											<Text
												color="TextLight"
												sm
												numberOfLines={1}
												ellipsizeMode="tail"
											>
												{song.artistName}
											</Text>
										</View>
									</View>
									<ToggleBadge isSelected={isSelected} variant="SingleSelect" />
								</OpacityView>
							</Touchable>
						);
					}}
				/>
			</View>
		</View>
	);
};
export default GatheringAppleMusicAddonEdit;
